import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./AddCategoryPopup.module.css";
import WebService from "../Services/WebService";
import {
  AddCategoryRequest,
  AddCategoryResponse,
  AddCategoryPopupProps,
  RawMaterialCategory
} from "../Models/Models";

interface AddCategoryPopupFullProps extends AddCategoryPopupProps {
  editCategory?: RawMaterialCategory | null;
  canWrite?: boolean;
  canUpdate?: boolean;
}

const AddCategoryPopup: React.FC<AddCategoryPopupFullProps> = ({
  onClose,
  onSuccess,
  parentCategories,
  editCategory = null,
  canWrite = false,
  canUpdate = false
}) => {
  const [form, setForm] = useState<AddCategoryRequest>({
    category_name: "",
    description: "",
    parent_category_id: undefined,
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Prefill for editing
  useEffect(() => {
    if (editCategory) {
      setForm({
        category_name: editCategory.category_name ?? "",
        description: editCategory.description ?? "",
        parent_category_id: editCategory.parent_category_id,
        image: null, // don't prefill image file
      });
    } else {
      setForm({
        category_name: "",
        description: "",
        parent_category_id: undefined,
        image: null,
      });
    }
    setError("");
  }, [editCategory]);

  // RBAC: auto-close if user opens without permission
  useEffect(() => {
    if ((editCategory && !canUpdate) || (!editCategory && !canWrite)) {
      setError(
        editCategory
          ? "You do not have permission to update categories."
          : "You do not have permission to add categories."
      );
      setTimeout(() => onClose(), 1000);
    }
    // eslint-disable-next-line
  }, [canWrite, canUpdate, editCategory, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  // Only number or undefined (no null)
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : undefined;
    setForm(prev => ({ ...prev, parent_category_id: value }));
    setError("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        setError("Only .jpg, .jpeg, or .png files allowed.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setError("File size should not exceed 2MB.");
        return;
      }
      setForm(prev => ({ ...prev, image: file }));
      setError("");
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // RBAC: Block if not allowed
    if (editCategory && !canUpdate) {
      setError("You do not have permission to update categories.");
      return;
    }
    if (!editCategory && !canWrite) {
      setError("You do not have permission to add categories.");
      return;
    }
    if (!form.category_name.trim()) {
      setError("Category name is required.");
      return;
    }
    setLoading(true);

    // For add/edit both, prepare FormData (image always supports both)
    const formData = new FormData();
    formData.append("category_name", form.category_name);
    formData.append("description", form.description);
    if (form.parent_category_id !== undefined) {
      formData.append("parent_category_id", String(form.parent_category_id));
    }
    if (form.image) formData.append("image", form.image);
    // Always pass type param!
    formData.append("type", "NO_SUB_CATEGORY");

    try {
      let res: AddCategoryResponse;
      if (editCategory) {
        // EDIT is POST with ?category_id=
        res = await WebService.postAPI({
          action: `/v1/door-management/raw-material/categories?category_id=${editCategory.category_id}`,
          body: formData,
          isFormData: true,
        }) as AddCategoryResponse;
      } else {
        // ADD
        res = await WebService.postAPI({
          action: "/v1/door-management/raw-material/categories",
          body: formData,
          isFormData: true,
        }) as AddCategoryResponse;
      }
      setLoading(false);

      if (res?.status_code === 201 || res?.status_code === 200) {
        if (onSuccess) onSuccess((res as any).category_id);
        onClose();
      } else {
        setError(res?.message || "Failed to add/edit category.");
      }
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err?.message || "Failed to add/edit category.");
    }
  };

  // Permissions: can user interact with form?
  const canInteract = editCategory ? canUpdate : canWrite;

  return ReactDOM.createPortal(
    <div className={styles.popupOverlay} onClick={handleOverlayClick}>
      <div className={styles.popupBox} onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h3 className={styles.heading}>
            {editCategory ? "Edit Category" : "Add Category"}
          </h3>
          <input
            className={styles.input}
            name="category_name"
            placeholder="Enter category name (e.g., Wood Panels, Metal Hardware)"
            value={form.category_name}
            onChange={handleChange}
            disabled={loading || !canInteract}
          />
          <label className={styles.descLabel}>Description</label>
          <textarea
            className={styles.textarea}
            name="description"
            placeholder="Provide a detailed description of this category and what materials it includes..."
            value={form.description}
            onChange={handleChange}
            disabled={loading || !canInteract}
          />
          <div className={styles.optional}>Optional: Add details about what materials belong to this category</div>
          <label className={styles.label}>Parent Category</label>
          <select
            className={styles.input}
            value={form.parent_category_id ?? ""}
            onChange={handleSelectChange}
            disabled={loading || !canInteract}
          >
            <option value="">None</option>
            {parentCategories.map(c => (
              <option value={c.category_id} key={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <label className={styles.label}>Image</label>
          <div className={styles.fileUploadWrap}>
            <input
              className={styles.fileInput}
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleFileChange}
              id="cat-img-upload"
              disabled={loading || !canInteract}
            />
            <label htmlFor="cat-img-upload" className={styles.fileFakeBtn}>
              Browse...
            </label>
            {form.image && (
              <span className={styles.fileName}>{form.image.name}</span>
            )}
          </div>
          {error && <div className={styles.errorMsg}>{error}</div>}
          <div className={styles.actionsRow}>
            <button className={styles.cancelBtn} type="button" onClick={onClose} disabled={loading}>Cancel</button>
            <button
              className={styles.addBtn}
              type="submit"
              disabled={loading || !canInteract}
              title={!canInteract ? "You do not have permission to perform this action" : undefined}
            >
              {loading
                ? (editCategory ? "Updating..." : "Adding...")
                : (editCategory ? "Update Category" : "+ Add Category")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddCategoryPopup;
